import { Button } from "@pepperstone/pepper-ui-kit";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { CashierTradingAccount } from "@/api/cashier";
import { usePlatformIcon } from "@/components/account-list/usePlatformIcon.ts";
import FilterDropdown from "@/components/filter-drop-down/FilterDropdown";
import style from "./TransactionHistoryTableFilter.module.css";
import type { OptionType, SelectedValuesType } from "./types";

export type TransactionHistoryTableFilterProps = {
  isLoading?: boolean;
  transactionOptions: OptionType[];
  timeOptions: OptionType[];
  statusOptions: OptionType[];
  accountOptions: OptionType[];
  paymentMethodOptions: OptionType[];
  currencyOptions: OptionType[];
  selectedValues: SelectedValuesType;
  setSelectedValues: (values: SelectedValuesType) => void;
  handleReset: () => void;
  btnIsDisabled: boolean;
};

export const PaymentMethodLabel = ({
  logo,
  name,
}: {
  logo: string;
  name: string;
}) => {
  return (
    <div className={style.labelWrapper}>
      <span className={style.iconContainer}>
        <img src={logo} alt={name} />
      </span>
      <span>{name}</span>
    </div>
  );
};

export const AccountLabel = ({
  tradingPlatform,
  accountId,
}: {
  tradingPlatform: CashierTradingAccount["tradingPlatform"];
  accountId: string;
}) => {
  const platformIcon = usePlatformIcon(tradingPlatform);
  return (
    <div className={style.labelWrapper}>
      <span className={style.iconContainer}>
        <img src={platformIcon} alt={tradingPlatform || "Pepperstone"} />
      </span>
      <span>{accountId}</span>
    </div>
  );
};

const TransactionHistoryTableFilter = ({
  isLoading,
  selectedValues,
  timeOptions,
  transactionOptions,
  paymentMethodOptions,
  accountOptions,
  currencyOptions,
  statusOptions,
  setSelectedValues,
  btnIsDisabled,
  handleReset,
}: TransactionHistoryTableFilterProps) => {
  const { t } = useTranslation();

  const handleSelect = useCallback(
    (key: keyof SelectedValuesType, value: string) => {
      const optionsMap = {
        time: timeOptions,
        transaction: transactionOptions,
        paymentMethod: paymentMethodOptions,
        account: accountOptions,
        currency: currencyOptions,
        status: statusOptions,
      };

      const newOption = optionsMap[key].find(
        (option) => option.value === value
      );
      if (!newOption) return;

      setSelectedValues({
        ...selectedValues,
        [key]: newOption,
      });
    },
    [
      timeOptions,
      transactionOptions,
      paymentMethodOptions,
      accountOptions,
      currencyOptions,
      statusOptions,
      setSelectedValues,
      selectedValues,
    ]
  );

  return (
    <div className={style.filterWrapper}>
      <FilterDropdown
        options={timeOptions}
        label={t("transactions.history.filter.time")}
        selectedValue={selectedValues.time.value}
        handleSelect={(value) => handleSelect("time", value)}
        disabled={isLoading}
      />
      <FilterDropdown
        options={transactionOptions}
        label={t("transactions.history.filter.transaction")}
        selectedValue={selectedValues.transaction.value}
        handleSelect={(value) => handleSelect("transaction", value)}
        disabled={isLoading}
      />
      <FilterDropdown
        options={paymentMethodOptions}
        label={t("transactions.history.filter.paymentMethod")}
        selectedValue={selectedValues.paymentMethod.value}
        handleSelect={(value) => handleSelect("paymentMethod", value)}
        disabled={isLoading}
      />
      <FilterDropdown
        options={accountOptions}
        label={t("transactions.history.filter.account")}
        selectedValue={selectedValues.account.value}
        handleSelect={(value) => handleSelect("account", value)}
        disabled={isLoading}
      />
      <FilterDropdown
        options={currencyOptions}
        label={t("transactions.history.filter.currency")}
        selectedValue={selectedValues.currency.value}
        handleSelect={(value) => handleSelect("currency", value)}
        disabled={isLoading || currencyOptions.length === 0}
      />
      <FilterDropdown
        options={statusOptions}
        label={t("transactions.history.filter.status")}
        selectedValue={selectedValues.status.value}
        handleSelect={(value) => handleSelect("status", value)}
        disabled={isLoading}
      />
      <Button
        type="button"
        size="ps-btn-md"
        variant="ps-btn-transparent"
        onClick={handleReset}
        disabled={isLoading || btnIsDisabled}
        className={style.resetButton}
        data-testid="resetFilterButton"
      >
        {t("transactions.history.filter.reset")}
      </Button>
    </div>
  );
};

export default TransactionHistoryTableFilter;
